import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Descriptions,
  Dropdown,
  Form,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import {
  DownOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  InboxOutlined,
  CalendarOutlined,
  DownloadOutlined,
  DeleteOutlined,
  EditOutlined,
  ThunderboltOutlined,
  ApiOutlined,
  CustomerServiceOutlined,
  ExperimentOutlined,
  PauseCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import {
  getRoute,
  operatorEditRouteShift,
  getRouteShiftEstimate,
  getRouteAuditLog,
  dismissRouteInconsistency,
  restoreRouteInconsistency,
  revertRouteShiftCorrection,
  revertRouteAutoClose,
  changeRouteDriver,
  operatorRouteAction,
} from '../modules/routes/api';
import { getDrivers } from '../modules/drivers/api';
import { rescheduleOrder } from '../modules/orders/api';
import { openDriverAppAsDriver } from '../utils/openDriverApp';
import { reannounceRouteWebhooks, resendRouteWebhooks } from '../modules/webhook-logs/api';
import { doDeleteRoute } from '../modules/routes/stores/thunk';
import DeleteRouteModal from '../components/Modals/DeleteRouteModal';
import PartnerLogo from '../components/PartnerLogo';
import { tenantConfig } from '../config/tenantConfig';
import CreateTicketModal from '../components/CreateTicketModal';
import EtaBacktestModal from '../components/EtaBacktestModal';
import RoutePausesModal from '../components/RoutePausesModal';
import DriverAvatar from '../components/DriverAvatar';
import { downloadRoutesCsv } from '../utils/routeCsvExport';
import { mergeRouteSteps } from '../utils/mergeRouteSteps';
import { setShouldFitbounds } from '../redux/slice';
import GoogleMap from '../components/Map/GoogleMap';
import Icon from '../icons';
import BreadCrumb from '../modules/breadcrumbs';
import { DATE_BACKEND_FORMAT, DAY_FORMAT, DAYTIME_FORMAT, TIME_FORMAT } from '../constants';
import './RouteDetail.scss';

// OVO-130 — unambiguous "Xh Ym" (the old decimal-hours "1.61 hr" read as a
// nonsensical "1h61"). Used for every duration KPI — shift, client service,
// and travel time — so they're all formatted consistently.
const formatMins = (mins) => {
  if (mins == null) return '-';
  if (mins <= 0) return '0m';
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};
const formatKm = (m) => (m != null ? `${(m / 1000).toFixed(2)} km` : '-');
// Feed addresses arrive multi-line; one line for a table cell.
const oneLineAddr = (a) =>
  String(a || '')
    .replace(/\s*[\r\n]+\s*/g, ', ')
    .replace(/(,\s*)+$/g, '')
    .replace(/,\s*,/g, ',');

// Shared KPI grid — the route summary card, the per-partner cards, and the
// "Driver point of view" card all render the same shape. `shiftMins` is only
// passed for the driver/total view (shift = payroll, not a per-partner number).
const renderKpiGrid = ({ stops, orders, distanceMeters, shiftMins, clientServiceMins, priseDeServiceMins, co2 }) => (
  <Row gutter={16}>
    <Col xs={12} sm={8} md={4}>
      <div className="kpi">
        <div className="kpi-label">Stops</div>
        <div className="kpi-value">{stops ?? 0}</div>
      </div>
    </Col>
    <Col xs={12} sm={8} md={4}>
      <div className="kpi">
        <div className="kpi-label">Orders</div>
        <div className="kpi-value">{orders ?? 0}</div>
      </div>
    </Col>
    <Col xs={12} sm={8} md={4}>
      <div className="kpi">
        <div className="kpi-label">Distance</div>
        <div className="kpi-value">{formatKm(distanceMeters)}</div>
      </div>
    </Col>
    {shiftMins != null && (
      <Col xs={12} sm={8} md={4}>
        <Tooltip title="Bike pick-up (start) → bike return (end) — for payroll / time-tracking">
          <div className="kpi">
            <div className="kpi-label">Shift Duration</div>
            <div className="kpi-value">{formatMins(shiftMins)}</div>
          </div>
        </Tooltip>
      </Col>
    )}
    {clientServiceMins != null && (
      <Col xs={12} sm={8} md={4}>
        <Tooltip title="First pickup (collection) → last delivery — for client billing">
          <div className="kpi">
            <div className="kpi-label">Client Service</div>
            <div className="kpi-value">{formatMins(clientServiceMins)}</div>
          </div>
        </Tooltip>
      </Col>
    )}
    {priseDeServiceMins != null && (
      <Col xs={12} sm={8} md={4}>
        <Tooltip title="Prise de service — bike pick-up / return + start & close overhead (shift − client service)">
          <div className="kpi">
            <div className="kpi-label">Prise de service</div>
            <div className="kpi-value">{formatMins(priseDeServiceMins)}</div>
          </div>
        </Tooltip>
      </Col>
    )}
    {co2 && (
      <Col xs={12} sm={8} md={4}>
        <Tooltip title={`${co2.gPerKm} g CO₂-eq per km · Mobitool v3.1 factor`}>
          <div className="kpi">
            <div className="kpi-label">CO₂</div>
            <div className="kpi-value">{co2.kg.toFixed(2)} kg</div>
          </div>
        </Tooltip>
      </Col>
    )}
    {co2?.savedKg > 0 && (
      <Col xs={12} sm={8} md={4}>
        <Tooltip
          title={`Compared to a reference vehicle at ${co2.baselineGPerKm} g CO₂-eq/km (default: diesel car, Mobitool v3.1)`}
        >
          <div className="kpi kpi-co2-saved">
            <div className="kpi-label" style={{ color: '#2e7d32', fontWeight: 600 }}>
              <span role="img" aria-label="eco" style={{ marginRight: 4 }}>
                🌱
              </span>
              CO₂ saved vs car
            </div>
            <div className="kpi-value" style={{ color: '#2e7d32', fontWeight: 700 }}>
              {co2.savedKg.toFixed(2)} kg
            </div>
          </div>
        </Tooltip>
      </Col>
    )}
  </Row>
);
const formatDateTime = (v) => (v ? moment(v).format(DAYTIME_FORMAT) : '-');
const formatDate = (v) => (v ? moment(v).format(DAY_FORMAT) : '-');
const formatShiftBoundary = (v) => {
  if (!v) return '-';
  const iso = String(v).includes('T');
  return iso ? moment(v).format(DAYTIME_FORMAT) : v;
};

// OVO-109 — Phase 1 live driver location.
// Initials are derived from the driver's name (first letters of each word,
// max 2 chars). Falls back to "?" so the pin always has something to show.
const driverInitials = (name) => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const letters = parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .filter(Boolean)
    .join('');
  return letters || '?';
};

// Pulsing-blue-circle marker shown at the driver's last known position.
// Pure presentational component; absolute-positioned pulse ring + solid
// core with the driver's initials. Animation is driven entirely by CSS
// (.driver-pin-pulse keyframes in RouteDetail.scss).
const DriverPin = ({ initials }) => (
  <div className="driver-pin" aria-label="Driver last known position">
    <span className="driver-pin-pulse" />
    <span className="driver-pin-core">{initials}</span>
  </div>
);

// Recursive JSON tree with collapsible nested objects/arrays
const renderScalar = (value) => {
  if (value === null) return <span className="json-null">null</span>;
  if (value === undefined) return <span className="json-undefined">undefined</span>;
  if (typeof value === 'string') return <span className="json-string">&quot;{value}&quot;</span>;
  if (typeof value === 'number') return <span className="json-number">{value}</span>;
  if (typeof value === 'boolean') return <span className="json-boolean">{String(value)}</span>;
  return <span>{String(value)}</span>;
};

const JsonNode = ({ value, name, depth = 0 }) => {
  const [collapsed, setCollapsed] = useState(depth > 0);
  const keyLabel = name != null && <span className="json-key">{String(name)}:</span>;

  if (value === null || value === undefined || typeof value !== 'object') {
    return (
      <span className="json-node">
        {keyLabel} {renderScalar(value)}
      </span>
    );
  }

  const isArray = Array.isArray(value);
  const entries = isArray ? value.map((v, i) => [i, v]) : Object.entries(value);
  const count = entries.length;
  const summary = isArray ? `Array(${count})` : `{${count}}`;

  return (
    <span className="json-node">
      <span className="json-toggle" onClick={() => setCollapsed(!collapsed)}>
        {collapsed ? '[+]' : '[-]'}
      </span>{' '}
      {keyLabel} {collapsed && <span className="json-summary">{summary}</span>}
      {!collapsed && (
        <div className="json-children">
          {entries.map(([k, v]) => (
            <div key={k} className="json-entry">
              <JsonNode value={v} name={k} depth={depth + 1} />
            </div>
          ))}
        </div>
      )}
    </span>
  );
};

const RouteDetailPage = () => {
  const { t } = useTranslation(['routes', 'common']);
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [route, setRoute] = useState(null);
  // OVO-203 — change-driver (swap courier without re-optimizing)
  const [changeDriverOpen, setChangeDriverOpen] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [pickedDriverId, setPickedDriverId] = useState(undefined);
  const [savingDriver, setSavingDriver] = useState(false);
  const [incDismissOpen, setIncDismissOpen] = useState(false);
  const [incDismissReason, setIncDismissReason] = useState('');
  const [incDismissSaving, setIncDismissSaving] = useState(false);
  // OVO-252 — reschedule a skipped/failed order straight from the stops table.
  const [reschedTarget, setReschedTarget] = useState(null); // orderCode
  const [reschedDate, setReschedDate] = useState(() => dayjs().add(1, 'day'));
  const [rescheduling, setRescheduling] = useState(false);
  const [loading, setLoading] = useState(true);
  // OVO-126 — route delete (with optional cascade) from the detail page.
  const [deleteOpen, setDeleteOpen] = useState(false);
  // OVO-128 — operator edit of the route's actual shift start/end.
  const [shiftOpen, setShiftOpen] = useState(false);
  const [shiftSubmitting, setShiftSubmitting] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [shiftForm] = Form.useForm();
  // Route edit-history (audit log) — lazy-loaded when the section is expanded.
  const [auditLog, setAuditLog] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  // Clicking a row in the Stops table highlights (blinks) its pin on the map.
  const [activeStop, setActiveStop] = useState(null);
  // Order hovered in the Stops table → both of its legs' rows light up.
  const [hoverOrderId, setHoverOrderId] = useState(null);
  // Sep 2026 — full-screen map (Diego: the 320px side map is too small to
  // read a whole route). The card itself goes `position: fixed`, so the map
  // instance is NOT remounted: pins, polyline and the active-stop highlight
  // survive the toggle. Bounds are re-fitted after the size change.
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const toggleMapFullscreen = () => setMapFullscreen((prev) => !prev);

  // Escape leaves full screen; the page behind must not scroll meanwhile.
  useEffect(() => {
    if (!mapFullscreen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setMapFullscreen(false);
    };
    document.body.classList.add('map-fullscreen-open');
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('map-fullscreen-open');
      window.removeEventListener('keydown', onKey);
    };
  }, [mapFullscreen]);

  // The card is resized by CSS, which Google Maps does NOT notice on its own:
  // without a resize event it keeps painting tiles at the old size (verified —
  // the map stayed a small square in the middle of the full-screen card). So
  // nudge it, then re-fit the bounds to the new viewport. Covers both the
  // button and Escape; skipped on mount (the route load fits already).
  const mapSizeRef = useRef(false);
  useEffect(() => {
    if (!mapSizeRef.current) {
      mapSizeRef.current = true;
      return undefined;
    }
    const resizeTimer = setTimeout(() => window.dispatchEvent(new Event('resize')), 220);
    const fitTimer = setTimeout(() => dispatch(setShouldFitbounds(true)), 420);
    return () => {
      clearTimeout(resizeTimer);
      clearTimeout(fitTimer);
    };
  }, [mapFullscreen, dispatch]);

  const loadAuditLog = async () => {
    if (auditLog || auditLoading) return;
    setAuditLoading(true);
    try {
      const { response } = await getRouteAuditLog(id);
      setAuditLog(Array.isArray(response) ? response : []);
    } catch {
      setAuditLog([]);
    } finally {
      setAuditLoading(false);
    }
  };

  // Mirrors the backend guard: deletable up to and including DISPATCHED;
  // blocked once the driver has started (IN_PROGRESS) or finished (COMPLETED).
  const isDeletable = ['UNASSIGNED', 'ASSIGNED', 'OPTIMIZED', 'DISPATCHED'].includes(route?.status?.toUpperCase());

  // OVO-128 — a route "needs fixing" when either actual boundary is missing
  // (driver forgot to badge start/end). Drives the red "Fix shift times"
  // affordance so operators spot it at a glance.
  const shiftNeedsFix = !!route && (!route.actualShiftStart || !route.actualShiftEnd);

  // OVO-128 — open the shift-time editor, seeding the form from the route's
  // current actual values. Stored as ISO datetimes → dayjs for the picker.
  const openShiftEditor = () => {
    shiftForm.setFieldsValue({
      actualShiftStart: route?.actualShiftStart ? dayjs(route.actualShiftStart) : null,
      actualShiftEnd: route?.actualShiftEnd ? dayjs(route.actualShiftEnd) : null,
      note: '',
    });
    setShiftOpen(true);
  };

  const handleSubmitShift = async () => {
    let values;
    try {
      values = await shiftForm.validateFields();
    } catch {
      return;
    }
    // Send explicit null to clear a boundary, ISO string to set it. Both
    // keys are always sent (the modal exposes both fields).
    const payload = {
      actualShiftStart: values.actualShiftStart ? values.actualShiftStart.toISOString() : null,
      actualShiftEnd: values.actualShiftEnd ? values.actualShiftEnd.toISOString() : null,
      ...(values.note ? { note: values.note } : {}),
    };
    setShiftSubmitting(true);
    const { response, error } = await operatorEditRouteShift(route.id, payload);
    setShiftSubmitting(false);
    if (error) {
      message.error(error?.data?.message || error?.message || t('shiftEditFailed'));
      return;
    }
    message.success(t('shiftEditSaved'));
    setShiftOpen(false);
    if (response) setRoute(response);
  };

  // Mark this route's inconsistency as a false positive (e.g. traffic jam).
  const submitIncDismiss = async () => {
    setIncDismissSaving(true);
    const { response, error } = await dismissRouteInconsistency(route.id, incDismissReason);
    setIncDismissSaving(false);
    if (error) {
      message.error(error?.data?.message || error?.message || t('common:error'));
      return;
    }
    message.success(t('inconsistencies.dismissed'));
    setIncDismissOpen(false);
    setIncDismissReason('');
    if (response) setRoute(response);
  };

  // #135 — undo the system's auto-close: the route is open again.
  const undoAutoClose = async () => {
    const { response, error } = await revertRouteAutoClose(route.id);
    if (error) {
      message.error(error?.data?.message || error?.message || t('common:error'));
      return;
    }
    message.success(t('autoClose.undone'));
    if (response) setRoute(response);
  };

  // OVO-260 — undo an auto-correction, restoring the driver's original badge.
  const undoShiftCorrection = async () => {
    const { response, error } = await revertRouteShiftCorrection(route.id);
    if (error) {
      message.error(error?.data?.message || error?.message || t('common:error'));
      return;
    }
    message.success(t('inconsistencies.correctionUndone', { id: route.id }));
    if (response) setRoute(response);
  };

  // Undo a false-positive dismissal — the route can be flagged again.
  const restoreInc = async () => {
    const { response, error } = await restoreRouteInconsistency(route.id);
    if (error) {
      message.error(error?.data?.message || error?.message || t('common:error'));
      return;
    }
    message.success(t('inconsistencies.restored'));
    if (response) setRoute(response);
  };

  // OVO-128 / OVO-129 (core) — one-click pre-fill of the modal from the
  // optimizer-derived estimate (first-stop arrival − hub leg, last-stop
  // finish + return leg). The operator still reviews + saves; nothing is
  // persisted by this call.
  const handleEstimateFromRoute = async () => {
    setEstimating(true);
    const { response, error } = await getRouteShiftEstimate(route.id);
    setEstimating(false);
    if (error) {
      message.error(error?.data?.message || error?.message || t('estimateFailed'));
      return;
    }
    if (!response?.actualShiftStart && !response?.actualShiftEnd) {
      message.warning(t('estimateNoStops'));
      return;
    }
    shiftForm.setFieldsValue({
      actualShiftStart: response.actualShiftStart ? dayjs(response.actualShiftStart) : null,
      actualShiftEnd: response.actualShiftEnd ? dayjs(response.actualShiftEnd) : null,
    });
    const src = response?.basis?.returnLegSource;
    message.success(src === 'symmetric' ? t('estimateAppliedApprox') : t('estimateApplied'));
  };

  const handleConfirmDelete = async (deleteOrders) => {
    try {
      await dispatch(doDeleteRoute({ id: route.id, deleteOrders })).unwrap();
      message.success(t('routeDeleted', { id: route.id }));
      navigate('/admin/routes');
    } catch (e) {
      message.error(e?.message || t('failedToDelete'));
    } finally {
      setDeleteOpen(false);
    }
  };

  const load = async () => {
    setLoading(true);
    const { response, error } = await getRoute(id);
    if (error) {
      message.error(error?.message || 'Failed to load route');
    } else {
      setRoute(response);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [id]);

  // OVO-203 — open the change-driver modal + lazy-load the driver list.
  const openChangeDriver = async () => {
    setPickedDriverId(route?.driver?.id);
    setChangeDriverOpen(true);
    const { response } = await getDrivers({ take: 500, page: 1 });
    const list = response?.drivers || response?.items || response || [];
    setDrivers(Array.isArray(list) ? list : []);
  };

  const handleChangeDriver = async () => {
    if (!pickedDriverId) {
      message.warning('Pick a driver');
      return;
    }
    setSavingDriver(true);
    const { error } = await changeRouteDriver(route.id, pickedDriverId);
    setSavingDriver(false);
    if (error) {
      message.error(error.message || 'Could not change driver');
      return;
    }
    message.success('Driver changed — same vehicle + stops kept (no re-optimization)');
    setChangeDriverOpen(false);
    load();
  };

  // OVO-117 — create a support ticket prefilled with this route + driver.
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  // OVO-266 — ETA backtest modal.
  const [backtestOpen, setBacktestOpen] = useState(false);
  // P1 Pause/Resume — pause manager modal
  const [pausesOpen, setPausesOpen] = useState(false);

  // OVO-226 — resend/backfill any dropped partner webhooks for this route's
  // orders. Default (no force) only sends the missing/failed ones (idempotent).
  const [resendingWh, setResendingWh] = useState(false);
  // Route 2639 (Sep 2026) — the old single "Resend" was force=false and silently
  // skipped every already-delivered order, so it could never repair the stale
  // partner positions left by a re-sequencing. Three explicit modes now.
  const [resendOpen, setResendOpen] = useState(false);
  const [resendMode, setResendMode] = useState('positions');
  // OVO-020 gap — the operator-route-action endpoint existed but had no UI:
  // an operator who unblocked a stuck route (e.g. 2260) had no way to CLOSE
  // it from the planner. The operator path is exempt from the open-stops
  // guard and stamps the badge-out at NOW (fix it afterwards via "Fix shift
  // times" if the driver actually finished earlier).
  const [closingRoute, setClosingRoute] = useState(false);
  const handleOperatorCloseRoute = async () => {
    setClosingRoute(true);
    const { error } = await operatorRouteAction(route.id, { actionStatus: 'COMPLETED' });
    setClosingRoute(false);
    if (error) {
      message.error(error?.data?.message || error?.message || t('common:error'));
      return;
    }
    message.success('Route closed (operator)');
    load();
  };

  const RESEND_MODES = [
    {
      value: 'positions',
      label: 'Changed positions (recommended)',
      help: 'Re-sends order.dispatched only to the orders whose position in the route (sort / relative order) differs from what the partner was last told — after a reorder, or a stop moved in or out. Idempotent.',
    },
    {
      value: 'missing',
      label: 'Missing only',
      help: 'Backfills orders that never got a delivered order.dispatched (dropped or failed calls). Already-delivered orders are skipped.',
    },
    {
      value: 'force',
      label: 'Force all',
      help: 'Re-sends order.dispatched to every partner order on the route, delivered or not.',
    },
  ];
  const handleResendWebhooks = async () => {
    setResendingWh(true);
    const { response, error } =
      resendMode === 'positions'
        ? await reannounceRouteWebhooks(route.id)
        : await resendRouteWebhooks(route.id, resendMode === 'force');
    setResendingWh(false);
    if (error) {
      message.error(error?.data?.message || error?.message || t('common:error'));
      return;
    }
    setResendOpen(false);
    const r = response || {};
    if (resendMode === 'positions') {
      if (!r.eligible) {
        message.info('Nothing to re-announce — the route is not dispatched (or already completed).');
      } else if (!r.changed) {
        message.success(`Positions already up to date — ${r.checked ?? 0} partner order(s) checked, none changed.`);
      } else {
        message.success(
          `Positions re-announced — ${r.resent ?? 0} re-sent (${r.delivered ?? 0} delivered, ${r.failed ?? 0} failed), ${r.unchanged ?? 0} unchanged, ${r.neverAnnounced ?? 0} never announced, ${r.skipped ?? 0} not eligible.`,
        );
      }
      return;
    }
    message.success(
      `Webhooks resent — ${r.delivered ?? 0} delivered, ${r.skipped ?? 0} skipped (already delivered or not eligible), ${r.failed ?? 0} failed (of ${r.orders ?? 0} partner orders)`,
    );
  };

  useEffect(() => {
    if (route) dispatch(setShouldFitbounds(true));
  }, [route, dispatch]);

  // OVO-109 — auto-refresh while the route is in flight, so the driver pin
  // moves as new OrderLog/RouteLog rows arrive (every status transition the
  // driver triggers from their phone updates the backend's `driverLastSeen`
  // computation). Polling stops automatically once the route is COMPLETED
  // or anything else, and on unmount.
  useEffect(() => {
    if (!route || route.status !== 'IN_PROGRESS') return undefined;
    const tick = setInterval(() => {
      // Reuse `load()` — it sets `route` via setRoute and refreshes the map
      // markers via the useMemo dependency on `route`.
      load();
    }, 30000);
    return () => clearInterval(tick);
    // We re-establish the interval whenever the route reference changes,
    // which it does after every successful poll. That's intentional —
    // ensures we re-evaluate the IN_PROGRESS gate after each fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route?.status, route?.id]);

  const { markers, polylines } = useMemo(() => {
    if (!route) return { markers: [], polylines: [] };
    const newMarkers = [];
    const locKey = (lat, lng) => `${Number(lat).toFixed(6)},${Number(lng).toFixed(6)}`;

    const coordsForStep = (step) => {
      const o = step.order;
      if (!o) return null;
      if (step.type === 'PICKUP') return { lat: o.pickupAddressLat, lng: o.pickupAddressLng };
      if (step.type === 'DELIVERY') return { lat: o.deliveryAddressLat, lng: o.deliveryAddressLng };
      if (step.type === 'SERVICE') return { lat: o.serviceAddressLat, lng: o.serviceAddressLng };
      return null;
    };

    // Group consecutive same-location stops exactly like the day-plan (depot
    // pickups → one numbered group), so the map numbering matches the Stops
    // table + the day-plan timeline (1..N, not the raw 1..10 step order).
    // Sep 2026 (Diego) — clicking a pin must answer "what does the driver do
    // here?", so each pin carries its stop(s) + their orders, not just coords.
    const oneLine = (v) =>
      String(v || '')
        .replace(/\s*[\r\n]+\s*/g, ', ')
        .replace(/(,\s*)+$/g, '')
        .replace(/,\s*,/g, ',');
    const windowOf = (o, type) => {
      const [a, b] =
        type === 'PICKUP'
          ? [o?.pickupTimeStart, o?.pickupTimeEnd]
          : type === 'SERVICE'
            ? [o?.serviceTimeStart, o?.serviceTimeEnd]
            : [o?.deliveryTimeStart, o?.deliveryTimeEnd];
      return a || b ? `${a || '…'} – ${b || '…'}` : null;
    };
    const stopPayload = (g) => {
      const type = g.type;
      const first = g.order || {};
      const orders = g.orders?.length ? g.orders : g.order ? [g.order] : [];
      const nameOf = (o) =>
        (type === 'PICKUP' ? o?.pickupName : type === 'SERVICE' ? o?.name || o?.serviceAddress : o?.deliveryName) ||
        o?.name ||
        '-';
      const addressOf = (o) =>
        type === 'PICKUP' ? o?.pickupAddress : type === 'SERVICE' ? o?.serviceAddress : o?.deliveryAddress;
      return {
        id: g.id,
        stopNumber: g.stopNumber,
        type,
        name: nameOf(first),
        address: oneLine(addressOf(first)),
        phone: type === 'PICKUP' ? first.pickupPhone : first.deliveryPhone,
        plannedArrival: g.originalArrivalTime || g.arrivalTime || null,
        actualArrival: g.actualArrivalTime || null,
        plannedFinish: g.originalFinishTime || g.finishTime || null,
        actualFinish: g.actualFinishTime || null,
        timeWindow: windowOf(first, type),
        orders: orders.map((o) => ({
          orderCode: o.orderCode,
          // The status of THIS leg — a pickup row must not show the delivery
          // outcome (OVO-251, same rule as the Stops table).
          status: type === 'PICKUP' ? o.pickupStatus || o.status : o.status,
          partner: o.partnerRef?.code || o.partnerRef?.name || null,
          box: o.collectionPoint
            ? o.collectionPoint.externalRef
              ? `Box ${o.collectionPoint.externalRef}`
              : o.collectionPoint.name
            : null,
          weight: o.weight ?? null,
          note: oneLine(type === 'PICKUP' ? o.pickupNote : o.deliveryNote) || null,
          description: oneLine(o.description) || null,
          driverNote: oneLine(o.customerNote || o.pickupMobileNote) || null,
          issueType: o.issueType || null,
          // Where this package goes next / came from — the context that makes
          // the stop actionable.
          otherLeg:
            type === 'PICKUP'
              ? { label: 'to', name: o.deliveryName, address: oneLine(o.deliveryAddress) }
              : type === 'DELIVERY'
                ? { label: 'from', name: o.pickupName, address: oneLine(o.pickupAddress) }
                : null,
        })),
      };
    };

    const groups = mergeRouteSteps(route.steps)
      .map((g) => {
        const c = coordsForStep(g);
        if (!c || c.lat == null || c.lng == null) return null;
        return {
          id: g.id,
          type: g.type,
          stopNumber: g.stopNumber,
          lat: Number(c.lat),
          lng: Number(c.lng),
          payload: stopPayload(g),
        };
      })
      .filter(Boolean);

    // Vehicle start/end depot coordinates — folded into a co-located stop pin.
    const startKey = route.startLat != null && route.startLng != null ? locKey(route.startLat, route.startLng) : null;
    const endKey = route.endLat != null && route.endLng != null ? locKey(route.endLat, route.endLng) : null;

    // Co-locate stops at the EXACT same coordinate into a single "1·4·7" pin
    // (mirrors the day-plan RouteMap) instead of stacking invisible duplicates.
    const byLoc = {};
    groups.forEach((m) => {
      const k = locKey(m.lat, m.lng);
      (byLoc[k] = byLoc[k] || []).push(m);
    });

    const foldedStart = new Set();
    const foldedEnd = new Set();
    Object.entries(byLoc).forEach(([key, list]) => {
      const ordered = [...list].sort((a, b) => a.stopNumber - b.stopNumber);
      const first = ordered[0];
      const isStart = key === startKey;
      const isEnd = key === endKey;
      if (isStart) foldedStart.add(key);
      if (isEnd) foldedEnd.add(key);

      if (ordered.length === 1 && !isStart && !isEnd) {
        newMarkers.push({
          id: first.id,
          stops: [first.payload],
          onClick: () => setActiveStop((prev) => (prev?.id === first.id ? null : { id: first.id })),
          content: (
            <Icon
              type={first.type === 'PICKUP' ? 'MarkerPickup' : 'MarkerDropoff'}
              text={first.stopNumber}
              color={'#fff'}
              strokeColor={'#4F8CE0'}
            />
          ),
          latitude: first.lat,
          longitude: first.lng,
          offsetX: 1,
          offsetY: -6,
        });
      } else {
        const label = ordered.map((m) => m.stopNumber).join('·');
        newMarkers.push({
          id: first.id,
          // Every Stops-table row this one pin stands for (its co-located
          // groups + the folded route start/end), so clicking any of them in
          // the table blinks this single pin.
          activeIds: [
            ...ordered.map((m) => m.id),
            ...(isStart ? [`${route.id}-start`] : []),
            ...(isEnd ? [`${route.id}-end`] : []),
          ],
          stops: ordered.map((m) => m.payload),
          isRouteStart: isStart,
          isRouteEnd: isEnd,
          onClick: () => setActiveStop((prev) => (prev?.id === first.id ? null : { id: first.id })),
          content: (
            <div className="merged-stop-badge" style={{ borderColor: '#4F8CE0', color: '#4F8CE0' }}>
              {isStart && <Icon type="RouteDetailsStart" color={'#4F8CE0'} className="badge-endpoint" />}
              <span className="badge-label">{label}</span>
              {isEnd && <Icon type="RouteDetailsEnd" color={'#4F8CE0'} className="badge-endpoint" />}
            </div>
          ),
          latitude: first.lat,
          longitude: first.lng,
          offsetX: 0,
          offsetY: 0,
        });
      }
    });

    // Separate start/end markers only where NOT folded into a stop pin.
    if (route.startLat && route.startLng && !foldedStart.has(startKey)) {
      newMarkers.push({
        id: `${route.id}-start`,
        content: <Icon type="MarkerStart" color={'#fff'} strokeColor={'#4F8CE0'} />,
        latitude: Number(route.startLat),
        longitude: Number(route.startLng),
        offsetX: 1,
        offsetY: -6,
      });
    }
    if (route.endLat && route.endLng && !foldedEnd.has(endKey)) {
      newMarkers.push({
        id: `${route.id}-end`,
        content: <Icon type="MarkerEnd" color={'#fff'} strokeColor={'#4F8CE0'} />,
        latitude: Number(route.endLat),
        longitude: Number(route.endLng),
        offsetX: 1,
        offsetY: -6,
      });
    }

    // OVO-109 — driver pin at the actual last GPS coordinate (no
    // projection / interpolation). The "between stops" cue is delivered
    // by inserting a virtual row in the Stops table below the map, which
    // is more accurate than a fake line on the map.
    if (route.driverLastSeen?.lat != null && route.driverLastSeen?.lng != null) {
      newMarkers.push({
        id: `${route.id}-driver`,
        content: <DriverPin initials={driverInitials(route.driver?.name)} />,
        latitude: Number(route.driverLastSeen.lat),
        longitude: Number(route.driverLastSeen.lng),
        offsetX: 0,
        offsetY: 0,
      });
    }

    const newPolylines = route.polyline ? [{ id: route.id, polyline: route.polyline, color: '#4F8CE0' }] : [];
    return { markers: newMarkers, polylines: newPolylines };
  }, [route]);

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  if (!route) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <p>Route not found</p>
        <Button onClick={() => navigate('/admin/routes')}>Back to Routes</Button>
      </div>
    );
  }

  const routeDate = route.date ? moment(route.date).format(DATE_BACKEND_FORMAT) : null;
  const fleet = route.schedulerForm?.fleet;
  const hub = fleet?.hub;

  // Level-2 restyle (Sep 2026) — every secondary action lives in ONE menu
  // (the old header was seven identical buttons in a row); confirmations
  // moved from Popconfirm to Modal.confirm since they now open from a menu.
  const actionStatusU = String(route.actionStatus || '').toUpperCase();
  const routeStatusU = String(route.status || '').toUpperCase();
  const canCloseRoute = actionStatusU !== 'COMPLETED' && actionStatusU !== 'PENDING';
  const dangerMenuItems = [
    ...(canCloseRoute
      ? [{ key: 'close', icon: <CheckCircleOutlined />, label: 'Close route (operator)', danger: true }]
      : []),
    ...(isDeletable ? [{ key: 'delete', icon: <DeleteOutlined />, label: t('deleteRoute'), danger: true }] : []),
  ];
  const actionMenuItems = [
    { key: 'csv', icon: <DownloadOutlined />, label: t('exportCsv') },
    { key: 'ticket', icon: <CustomerServiceOutlined />, label: t('tickets.createFromHere') },
    { key: 'backtest', icon: <ExperimentOutlined />, label: 'ETA backtest' },
    {
      key: 'pauses',
      icon: <PauseCircleOutlined />,
      label: `Pauses${route?.durations?.pauseCount > 0 ? ` (${route.durations.pauseCount})` : ''}`,
    },
    { key: 'webhooks', icon: <ApiOutlined />, label: 'Resend webhooks' },
    ...(routeStatusU !== 'COMPLETED' ? [{ key: 'driver', icon: <EditOutlined />, label: 'Change driver' }] : []),
    ...(dangerMenuItems.length ? [{ type: 'divider' }, ...dangerMenuItems] : []),
  ];
  const onActionMenuClick = ({ key }) => {
    if (key === 'csv') {
      downloadRoutesCsv([route], { hr: true });
      message.success(t('exportSuccess', { count: 1 }));
    } else if (key === 'ticket') setTicketModalOpen(true);
    else if (key === 'backtest') setBacktestOpen(true);
    else if (key === 'pauses') setPausesOpen(true);
    else if (key === 'driver') openChangeDriver();
    else if (key === 'delete') setDeleteOpen(true);
    else if (key === 'webhooks') setResendOpen(true);
    else if (key === 'close')
      Modal.confirm({
        title: 'Close this route as operator?',
        content:
          "Marks the route COMPLETED on the driver's behalf (open stops are bypassed — the driver guard does not apply to operators). The badge-out is stamped at NOW; correct it afterwards with Fix shift times if needed.",
        okText: 'Close route',
        okButtonProps: { danger: true },
        cancelText: t('common:cancel'),
        onOk: handleOperatorCloseRoute,
      });
  };

  // Start punctuality (Felipe, Aug 2026 — same rule as the driver stats):
  // badged start vs the planned shift start on the route's own day.
  const startDelayMins = (() => {
    if (!route.actualShiftStart || !route.shiftStart || !route.date) return null;
    const planned = moment(`${moment(route.date).format('YYYY-MM-DD')} ${route.shiftStart}`, 'YYYY-MM-DD HH:mm');
    if (!planned.isValid()) return null;
    const d = moment(route.actualShiftStart).diff(planned, 'minutes');
    return Number.isFinite(d) && Math.abs(d) < 24 * 60 ? d : null;
  })();
  const orderStatusCount = (statuses) =>
    (route.orders || []).filter((o) => statuses.includes(String(o.status || '').toUpperCase())).length;

  const breadCrumbs = [{ text: 'Routes', url: '/admin/routes' }, { text: `Route #${route.id}` }];

  // OVO-109 — synthetic "driver" row injected into the Stops table at the
  // position the driver is currently at (i.e. between the last COMPLETED
  // step and the next active step). Renders only when we have a
  // driverLastSeen event; absent for routes that are dispatched but where
  // the driver hasn't started.
  // OVO-128 — synthetic START / END boundary rows so the operator can see
  // (and, via the editor, fix) the shift start/end that bookend the real
  // stops. The depot/driver leg isn't stored as a RouteStep, so we derive
  // these from the route's address + shift fields. The arrival column shows
  // planned (shiftStart/End, HH:MM) → actual (actualShiftStart/End, ISO).
  const startBoundaryRow = {
    id: `__start__${route.id}`,
    isBoundaryRow: true,
    boundary: 'START',
    type: 'DRIVER',
    driverName: route.driver?.name,
    address: route.startAddress,
    plannedTime: route.shiftStart,
    actualTime: route.actualShiftStart,
    // Did the driver badge in? (drives the Badged/Not-badged status tag)
    badged: !!route.actualShiftStart,
  };
  // OVO-128 follow-up — the END row shows the ETA of the driver arriving back
  // at the hub (last delivery + return leg), not the shift badge-out. The
  // badge-out time (`actualShiftEnd`, payroll) can be well after the driver
  // physically returned, and after a late run the reconciled shift end could
  // even fall before the last delivery — which read as "route ended before its
  // last stop". `route.hubArrival` is the depot-arrival ETA (backend-derived
  // from the actual last-stop finish); the badge-out stays in the shift panel.
  const endBoundaryRow = {
    id: `__end__${route.id}`,
    isBoundaryRow: true,
    boundary: 'END',
    type: 'DRIVER',
    driverName: route.driver?.name,
    address: route.endAddress,
    plannedTime: route.hubArrival?.plannedTime ?? route.shiftEnd,
    actualTime: route.hubArrival?.actualTime ?? null,
    isEta: true, // the actual is a computed hub-arrival ETA, not a badge
    badgeOutTime: route.actualShiftEnd, // shown as a secondary line
    badged: !!route.actualShiftEnd,
  };

  // Group consecutive same-location stops (depot pickups → one row) so the Stops
  // table, the count, and the map pins all match the day-plan timeline (1..N).
  const mergedStops = mergeRouteSteps(route.steps);

  // Empties collected per stop (Diego, Aug 2026) — EMPTY-category collected
  // items matched to their stop: routeStepId first, orderId as fallback for
  // items the driver logged without a step (shown on the delivery leg only,
  // so they don't double up on the pickup row).
  const emptyItems = (route.collectedItems || []).filter((ci) => ci.partnerItemType?.category === 'EMPTY');
  const emptiesForRec = (rec) => {
    if (!emptyItems.length || rec.isBoundaryRow || rec.isDriverRow) return [];
    const stepIds = new Set((rec.mergedSteps || [rec]).map((st) => st.id));
    const oIds = new Set(rec.orderIds || (rec.orderId != null ? [rec.orderId] : []));
    return emptyItems.filter((ci) =>
      ci.routeStepId != null
        ? stepIds.has(ci.routeStepId)
        : rec.type !== 'PICKUP' && ci.orderId != null && oIds.has(ci.orderId),
    );
  };

  // The map-marker id a Stops-table row corresponds to (so a row click can
  // blink the right pin). Stop rows use their step id (which a merged pin lists
  // in `activeIds`); the START/END boundary + driver rows use the synthetic
  // marker ids.
  const stopRowMapId = (rec) =>
    rec.isBoundaryRow
      ? `${route.id}-${rec.boundary === 'START' ? 'start' : 'end'}`
      : rec.isDriverRow
        ? `${route.id}-driver`
        : rec.id;

  // Where each order's OTHER leg sits (Diego, Sep 2026): a pickup row lists
  // the delivery stop each of its orders goes to (name, address, stop #), a
  // delivery row the pickup it came from — so a 5-order depot pickup no longer
  // reads as five anonymous codes. Built from the SAME grouped stops the table
  // renders, so the numbers match the `#` column and the map pins.
  const legStopByOrderId = { PICKUP: new Map(), DELIVERY: new Map() };
  mergedStops.forEach((g) => {
    if (!legStopByOrderId[g.type]) return;
    (g.orderIds || []).forEach((oid) => legStopByOrderId[g.type].set(oid, g));
  });
  // Jump to a stop row: select it (its pin blinks) + scroll it into view.
  const focusStopRow = (g) => {
    if (!g) return;
    setActiveStop({ id: g.id });
    // Instant, not smooth: a smooth scroll is silently dropped by some
    // embedded/emulated browsers, and the row must be there when the eye
    // follows the click.
    const row = document.querySelector(`.steps-card tr[data-row-key="${g.id}"]`);
    if (row && typeof row.scrollIntoView === 'function') row.scrollIntoView({ block: 'center' });
  };

  const stepsWithDriver = (() => {
    const baseSteps = mergedStops;
    const last = route.driverLastSeen;

    let sequence = baseSteps;
    if (last && last.lat != null && last.lng != null) {
      // Index of the first non-completed step (the one the driver is heading to).
      const nextIdx = baseSteps.findIndex((s) => s.type !== 'DRIVER' && s.status !== 'COMPLETED');
      // If everything is done, append at the end ("route finished, driver
      // last seen at …"); else splice before the first non-completed step.
      const insertAt = nextIdx === -1 ? baseSteps.length : nextIdx;
      const nextStep = nextIdx === -1 ? null : baseSteps[nextIdx];

      const driverRow = {
        id: `__driver__${route.id}`,
        isDriverRow: true,
        type: 'DRIVER',
        driverName: route.driver?.name,
        lastSeenAt: last.at,
        eventStatus: last.eventStatus,
        sourceOrderCode: last.orderCode,
        nextStep, // for the location label
      };

      sequence = [...baseSteps.slice(0, insertAt), driverRow, ...baseSteps.slice(insertAt)];
    }

    return [startBoundaryRow, ...sequence, endBoundaryRow];
  })();

  // Format an actual (ISO) timestamp: just the time when it's on the same day
  // as the route's START, the full date when it differs (the "driver finished /
  // badged the next day" case we want to make obvious). The anchor is the
  // ACTUAL shift start — NOT `route.date`, which can be disconnected from when
  // the work was really done (re-dated / test routes), making every same-day
  // actual wrongly render with a full date.
  const routeAnchorDay = route.actualShiftStart
    ? moment(route.actualShiftStart)
    : route.date
      ? moment(route.date)
      : null;
  const formatActualTime = (ts) => {
    const m = moment(ts);
    if (!m.isValid()) return null;
    const sameDay = routeAnchorDay && m.isSame(routeAnchorDay, 'day');
    return m.format(sameDay ? TIME_FORMAT : DAYTIME_FORMAT);
  };

  // OVO-128 — planned → actual shift-boundary cell for the START/END rows.
  const renderBoundaryTime = (rec) => {
    const planned = rec.plannedTime ? formatShiftBoundary(rec.plannedTime) : null;
    // Actual badged time: just the time on the start's day, full date otherwise.
    let actual = null;
    if (rec.actualTime) {
      actual = formatActualTime(rec.actualTime) ?? formatShiftBoundary(rec.actualTime);
    }
    // OVO-128 follow-up — the END row's "actual" is a computed hub-arrival ETA
    // (last delivery + return leg), not a badge. Flag it as "(ETA)" and surface
    // the real badge-out time as a secondary line so payroll info isn't lost.
    let badgeOut = null;
    if (rec.isEta && rec.badgeOutTime) {
      badgeOut = formatActualTime(rec.badgeOutTime);
    }
    if (!planned && !actual && !badgeOut) return <span style={{ color: '#bbb' }}>{t('notRecorded')}</span>;
    return (
      <div style={{ lineHeight: 1.35 }}>
        {planned && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {t('plannedLabel')} {planned}
          </div>
        )}
        {actual && (
          <div>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('actualLabel')} </span>
            {actual}
            {rec.isEta && (
              <Tooltip title={t('hubArrivalEtaHint')}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 4 }}>({t('etaLabel')})</span>
              </Tooltip>
            )}
          </div>
        )}
        {badgeOut && (
          <div style={{ fontSize: 11, color: '#bbb' }}>
            {t('badgedOutLabel')} {badgeOut}
          </div>
        )}
      </div>
    );
  };

  // OVO-140 — planned → actual cell for a numbered stop. `plannedStr` is the
  // optimizer's HH:MM (route_steps.arrival/finish_time); `actualTs` is the ISO
  // timestamp the driver actually arrived/finished (derived from order logs).
  // Mirrors the START/END boundary-row style (renderBoundaryTime).
  const renderStopTime = (plannedStr, actualTs) => {
    const actual = actualTs ? formatActualTime(actualTs) : null;
    if (!plannedStr && !actual) return '-';
    return (
      <div style={{ lineHeight: 1.35 }}>
        {plannedStr && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {t('plannedLabel')} {plannedStr}
          </div>
        )}
        {actual && (
          <div>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('actualLabel')} </span>
            {actual}
          </div>
        )}
      </div>
    );
  };

  // Renderer helper: produce a "where the driver is right now" string for
  // the location column on the synthetic row.
  const formatDriverWhereabouts = (rec) => {
    const ago = rec.lastSeenAt ? moment(rec.lastSeenAt).fromNow() : null;
    const heading = rec.nextStep ? `→ heading to stop #${rec.nextStep.orderNumber}` : 'route finished';
    const ctx = rec.sourceOrderCode
      ? ` after ${String(rec.eventStatus || '')
          .replace(/_/g, ' ')
          .toLowerCase()} of ${rec.sourceOrderCode}`
      : rec.eventStatus
        ? ` after route ${String(rec.eventStatus).toLowerCase()}`
        : '';
    return ago ? `${heading} — last seen ${ago}${ctx}` : heading;
  };

  const routeHasEmpties = (stepsWithDriver || []).some((rec) => emptiesForRec(rec).length > 0);

  const stepColumns = [
    {
      title: '#',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: 50,
      render: (v, rec) => {
        if (rec.isBoundaryRow)
          return <span className="stop-num boundary">{rec.boundary === 'START' ? '▶' : '■'}</span>;
        return rec.isDriverRow ? (
          <DriverPin initials={driverInitials(rec.driverName)} />
        ) : (
          <span className={'stop-num ' + String(rec.type || '').toLowerCase()}>{v}</span>
        );
      },
    },
    {
      title: t('stopClicked.type'),
      dataIndex: 'type',
      key: 'type',
      width: 110,
      render: (v, rec) => {
        if (rec.isBoundaryRow)
          return (
            <span className="stop-chip boundary">{rec.boundary === 'START' ? t('routeStart') : t('routeEnd')}</span>
          );
        if (rec.isDriverRow) return <span className="stop-chip driver">Driver</span>;
        const count = rec.orderIds?.length || 1;
        const label =
          String(v || '').charAt(0) +
          String(v || '')
            .slice(1)
            .toLowerCase();
        return (
          <span className={'stop-chip ' + String(v || '').toLowerCase()}>
            {count > 1 ? `Multi-${label.toLowerCase()} · ${count}` : label}
          </span>
        );
      },
    },
    {
      title: 'Stop',
      key: 'name',
      render: (_, rec) => {
        // Name + address stacked in one cell (the two columns used to fight
        // for width). For a merged stop, the shared site/contact name.
        const addrOf = () => {
          if (rec.isBoundaryRow) return rec.address || '';
          if (rec.isDriverRow) return formatDriverWhereabouts(rec);
          const o = rec.order;
          if (!o) return '';
          if (rec.type === 'PICKUP') return o.pickupAddress || '';
          if (rec.type === 'DELIVERY') return o.deliveryAddress || '';
          if (rec.type === 'SERVICE') return o.serviceAddress || '';
          return '';
        };
        const name = rec.isBoundaryRow
          ? rec.driverName || t('depot')
          : rec.isDriverRow
            ? rec.driverName || 'Driver'
            : (rec.type === 'PICKUP'
                ? rec.order?.pickupName
                : rec.type === 'DELIVERY'
                  ? rec.order?.deliveryName
                  : null) ||
              rec.order?.name ||
              rec.name ||
              '-';
        const addr = String(addrOf() || '')
          .replace(/\s*[\r\n]+\s*/g, ', ')
          .replace(/(,\s*)+$/g, '')
          .replace(/,\s*,/g, ',');
        return (
          <div className="stop-cell">
            <div className="stop-name" title={name}>
              {name}
            </div>
            {addr && (
              <div className="stop-addr" title={addr}>
                {addr}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: t('stopClicked.arrivalTime'),
      key: 'arrivalTime',
      width: 160,
      render: (_, rec) => {
        if (rec.isBoundaryRow) return renderBoundaryTime(rec);
        if (rec.isDriverRow) return rec.lastSeenAt ? moment(rec.lastSeenAt).format(DAYTIME_FORMAT) : '-';
        // Planned = optimizer ETA (arrival_time); Actual = order-log timestamp.
        return renderStopTime(rec.originalArrivalTime || rec.arrivalTime, rec.actualArrivalTime);
      },
    },
    {
      title: t('stopClicked.finishTime'),
      key: 'finishTime',
      width: 160,
      render: (_, rec) => {
        if (rec.isBoundaryRow) return '-';
        if (rec.isDriverRow) return '-';
        return renderStopTime(rec.originalFinishTime || rec.finishTime, rec.actualFinishTime);
      },
    },
    {
      // #114 — the planned on-site minutes and where they come from (task per
      // order + stop overhead once per stop), so a 5-min stop against a
      // 0.25-min partner parameter explains itself.
      title: <Tooltip title={t('onSiteHint')}>{t('onSite')}</Tooltip>,
      key: 'onSite',
      width: 110,
      render: (_, rec) => {
        if (rec.isBoundaryRow || rec.isDriverRow) return '-';
        const members = (rec.mergedSteps || [rec]).filter((m) => m.serviceTime);
        if (!members.length) return '-';
        const fmtMin = (n) => `${Math.round(n * 100) / 100}`;
        const sourceLabel = (src, step) => t(`onSiteSource.${src}`, { code: step?.order?.partnerRef?.code || '' });
        const total = members.reduce((acc, m) => acc + m.serviceTime.totalMins, 0);
        const taskGroups = new Map();
        members.forEach((m) => {
          const k = `${m.serviceTime.taskMins}|${m.serviceTime.taskSource}|${m.order?.partnerRef?.code || ''}`;
          const g = taskGroups.get(k) || { n: 0, step: m };
          g.n += 1;
          taskGroups.set(k, g);
        });
        const overheadStep = members.find((m) => m.serviceTime.overheadMins > 0);
        const tip = (
          <div>
            {[...taskGroups.values()].map((g, i) => (
              <div key={i}>
                {t('onSiteTask', {
                  detail: `${g.n > 1 ? `${g.n} × ` : ''}${fmtMin(g.step.serviceTime.taskMins)} min (${sourceLabel(
                    g.step.serviceTime.taskSource,
                    g.step,
                  )})`,
                })}
              </div>
            ))}
            {overheadStep && (
              <div>
                {t('onSiteStop', {
                  mins: fmtMin(overheadStep.serviceTime.overheadMins),
                  source: sourceLabel(overheadStep.serviceTime.overheadSource, overheadStep),
                })}
              </div>
            )}
          </div>
        );
        return (
          <Tooltip title={tip}>
            <span data-testid="on-site-mins" style={{ borderBottom: '1px dotted var(--text-muted)' }}>
              {fmtMin(total)} min
            </span>
          </Tooltip>
        );
      },
    },
    {
      // Debugging aid (Aug 2026): the ORDER's time window for this leg, so a
      // plan clamped to a window start (e.g. a retour stuck at 13:00) is
      // explained right next to the planned times it produced.
      title: t('timeWindow'),
      key: 'timeWindow',
      width: 120,
      render: (_, rec) => {
        if (rec.isBoundaryRow || rec.isDriverRow) return '-';
        const windowOf = (o) => {
          if (!o) return null;
          let s;
          let e;
          if (rec.type === 'PICKUP') {
            s = o.pickupTimeStart;
            e = o.pickupTimeEnd;
          } else if (rec.type === 'SERVICE') {
            s = o.serviceTimeStart;
            e = o.serviceTimeEnd;
          } else {
            s = o.deliveryTimeStart;
            e = o.deliveryTimeEnd;
          }
          if (!s && !e) return null;
          return `${s || '…'} – ${e || '…'}`;
        };
        const orders = rec.orders?.length ? rec.orders : rec.order ? [rec.order] : [];
        const windows = [...new Set(orders.map(windowOf).filter(Boolean))];
        if (!windows.length) return <span style={{ color: '#bbb' }}>{t('noTimeWindow')}</span>;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {windows.map((w) => (
              <span key={w}>{w}</span>
            ))}
          </div>
        );
      },
    },
    {
      // OVO-251 — "Stop status", not "Status": this is the STEP's handling
      // state, and it sat next to the order links reading as the order outcome.
      title: t('stopStatus'),
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (v, rec) => {
        if (rec.isBoundaryRow)
          return rec.badged ? <Tag color="success">{t('badged')}</Tag> : <Tag color="error">{t('notBadged')}</Tag>;
        if (rec.isDriverRow) return <Tag color="processing">IN_TRANSIT</Tag>;
        // OVO-251 — same badge style/palette as /admin/orders (the global
        // `.status.order.<value>` SCSS), instead of a diverging AntD Tag set.
        if (!v) return '-';
        return (
          <span className={'status order ' + String(v).toLowerCase()}>
            {String(v).toLowerCase().replace(/_/g, ' ')}
          </span>
        );
      },
    },
    ...(routeHasEmpties
      ? [
          {
            title: t('emptiesCollected'),
            key: 'empties',
            width: 150,
            render: (_, rec) => {
              const items = emptiesForRec(rec);
              if (!items.length) return '-';
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {items.map((ci) => (
                    <Tooltip
                      key={ci.id}
                      title={`${ci.partnerItemType?.name || ''} · ${dayjs(ci.collectedAt || ci.createdAt).format('HH:mm')}${
                        ci.weight != null ? ` · ${ci.weight} kg` : ''
                      }`}
                    >
                      <Tag color="green" style={{ marginInlineEnd: 0, width: 'fit-content' }}>
                        ♻️ {ci.quantity || 1}× {ci.partnerItemType?.name || '?'}
                      </Tag>
                    </Tooltip>
                  ))}
                </div>
              );
            },
          },
        ]
      : []),
    {
      title: 'Order',
      key: 'orderLink',
      width: 330,
      render: (_, rec) => {
        if (rec.isBoundaryRow || rec.isDriverRow) return '-';
        // A grouped row can cover several orders at the same stop (e.g. the
        // depot pickups) — list each as its own link.
        const orders = (rec.orders?.length ? rec.orders : rec.order ? [rec.order] : []).filter((o) => o?.orderCode);
        if (!orders.length) return '-';
        // OVO-251 — the step status ("COMPLETED" = handled) is NOT the order
        // outcome (an order can be SKIPPED on a completed step). Show each
        // order's status FOR THIS LEG next to its code: pickup rows show the
        // pickup outcome, delivery/service rows the order status — with the
        // exact same badge classes as the Orders page.
        const legStatus = (o) => (rec.type === 'PICKUP' ? o.pickupStatus || o.status : o.status);
        // The order's OTHER leg: pickup rows → "→ to <delivery>", delivery
        // rows → "← from <pickup>"; a SERVICE visit has no other leg.
        const otherLegOf = (o) => {
          if (rec.type === 'PICKUP')
            return { leg: 'DELIVERY', label: t('infoWindow.goesTo'), name: o.deliveryName, address: o.deliveryAddress };
          if (rec.type === 'DELIVERY')
            return { leg: 'PICKUP', label: t('infoWindow.comesFrom'), name: o.pickupName, address: o.pickupAddress };
          return null;
        };
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {orders.map((o) => {
              const s = legStatus(o);
              const other = otherLegOf(o);
              const otherStop = other ? legStopByOrderId[other.leg].get(o.id) : null;
              const otherAddr = other ? oneLineAddr(other.address) : '';
              return (
                <div
                  key={o.orderCode}
                  className="order-row"
                  onMouseEnter={() => setHoverOrderId(o.id)}
                  onMouseLeave={() => setHoverOrderId(null)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Link to={`/admin/orders/${o.orderCode}`} onClick={(e) => e.stopPropagation()}>
                      {o.orderCode}
                    </Link>
                    {/* CT — the linked collection box, explicit and clickable
                      (pickup leg only: the box lives at the pickup site). */}
                    {rec.type === 'PICKUP' && o.collectionPoint && (
                      <Link
                        to={`/admin/collection-points/${o.collectionPoint.id}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Tag color="geekblue" style={{ marginInlineEnd: 0, fontSize: 11, cursor: 'pointer' }}>
                          {o.collectionPoint.externalRef
                            ? `Box ${o.collectionPoint.externalRef}`
                            : o.collectionPoint.name}
                        </Tag>
                      </Link>
                    )}
                    {s && (
                      <span
                        className={'status order ' + String(s).toLowerCase()}
                        style={{ fontSize: 11, padding: '1px 6px', minWidth: 0 }}
                      >
                        {t(`common:orderStatuses.${s}`, String(s).replace(/_/g, ' '))}
                      </span>
                    )}
                    {/* OVO-252 — one-click reschedule of a skipped/failed order,
                      right where the operator sees the red badge (the failed
                      leg only — not next to the green "Collected" pickup). */}
                    {['SKIPPED', 'DELIVERY_FAILED', 'PICKUP_FAILED'].includes(String(s)) && (
                      <Tooltip title="Reschedule to a new date (the order leaves this route)">
                        <Button
                          size="small"
                          type="link"
                          icon={<CalendarOutlined />}
                          style={{ padding: 0, height: 'auto' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setReschedDate(dayjs().add(1, 'day'));
                            setReschedTarget(o.orderCode);
                          }}
                        >
                          Reschedule
                        </Button>
                      </Tooltip>
                    )}
                  </div>
                  {other && (other.name || otherAddr) && (
                    <div className="order-leg" title={[other.name, otherAddr].filter(Boolean).join(' — ')}>
                      <span className="leg-label">{other.label}</span>
                      {otherStop && (
                        <Tooltip title={t('goToStop', { n: otherStop.stopNumber })}>
                          <span
                            className={'stop-num sm ' + other.leg.toLowerCase()}
                            onClick={(e) => {
                              e.stopPropagation();
                              focusStopRow(otherStop);
                            }}
                          >
                            {otherStop.stopNumber}
                          </span>
                        </Tooltip>
                      )}
                      {other.name && <span className="leg-name">{other.name}</span>}
                      {otherAddr && <span className="leg-addr">{otherAddr}</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      },
    },
  ];

  const orderColumns = [
    {
      title: t('routeOrdersTable.orderId'),
      dataIndex: 'orderCode',
      key: 'orderCode',
      width: 120,
      render: (v) => (v ? <Link to={`/admin/orders/${v}`}>{v}</Link> : '-'),
    },
    {
      title: t('routeOrdersTable.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('partners:title', 'Partner'),
      key: 'partner',
      render: (_, rec) => rec.partnerRef?.name || rec.partner || '-',
    },
    {
      title: t('routeOrdersTable.deliveryAddress'),
      dataIndex: 'deliveryAddress',
      key: 'deliveryAddress',
      ellipsis: true,
    },
    {
      title: t('common:status'),
      dataIndex: 'status',
      key: 'status',
      width: 190,
      // OVO-251 — same badge style/palette as /admin/orders (was a plain
      // colorless AntD Tag). OVO-252 — skipped/failed orders get the same
      // one-click Reschedule as the stops table.
      render: (v, rec) =>
        v ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span className={'status order ' + String(v).toLowerCase()}>
              {t(`common:orderStatuses.${v}`, String(v).replace(/_/g, ' '))}
            </span>
            {['SKIPPED', 'DELIVERY_FAILED', 'PICKUP_FAILED'].includes(String(v)) && (
              <Tooltip title="Reschedule to a new date (the order leaves this route)">
                <Button
                  size="small"
                  type="link"
                  icon={<CalendarOutlined />}
                  style={{ padding: 0, height: 'auto' }}
                  onClick={() => {
                    setReschedDate(dayjs().add(1, 'day'));
                    setReschedTarget(rec.orderCode);
                  }}
                >
                  Reschedule
                </Button>
              </Tooltip>
            )}
          </div>
        ) : (
          '-'
        ),
    },
  ];

  return (
    <div className="RouteDetailPage Content">
      <div className="ContentHeader">
        <BreadCrumb breadCrumbs={breadCrumbs} />
      </div>

      <div className="route-detail-content">
        {/* Hero — who / what / when at a glance; every secondary action in one menu. */}
        <div className="route-hero">
          <DriverAvatar name={route.driver?.name} photoKey={route.driver?.photoFile?.key} size={52} />
          <div className="route-hero-main">
            <div className="route-hero-title">
              <span className="route-hero-name">
                Route #{route.id}
                {route.driver?.name && (
                  <>
                    {' · '}
                    {route.driver.id ? (
                      <Link to={`/admin/drivers/${route.driver.id}`}>{route.driver.name}</Link>
                    ) : (
                      route.driver.name
                    )}
                  </>
                )}
              </span>
              {route.status && (
                <span className={'status order ' + String(route.status).toLowerCase()}>
                  {String(route.status).toLowerCase().replace(/_/g, ' ')}
                </span>
              )}
              {route.actionStatus && !['PENDING', 'COMPLETED'].includes(actionStatusU) && (
                <span className="status order in_progress">{String(route.actionStatus).toLowerCase()}</span>
              )}
            </div>
            <div className="route-hero-meta">
              <span>{formatDate(route.date)}</span>
              {fleet?.name && (
                <span>
                  {fleet.name}
                  {fleet.tag ? ` · ${String(fleet.tag).toLowerCase()}` : ''}
                </span>
              )}
              {hub?.name && <span>Hub {hub.name}</span>}
              {route.driver?.phone && <span>{route.driver.phone}</span>}
              {/* OVO-132 single-tenant: one implicit partner — no chips. */}
              {!tenantConfig.isSingleTenant && route.partners?.length > 0 && (
                <span className="route-hero-partners">
                  {route.partners.map((p) => (
                    <Tooltip key={p.id || p.code || p.name} title={p.name}>
                      <span className="partner-chip">
                        <PartnerLogo name={p.name} logoKey={p.logoFile?.key} size={16} />
                        {p.code || p.name}
                      </span>
                    </Tooltip>
                  ))}
                </span>
              )}
            </div>
          </div>
          <div className="route-hero-actions">
            {/* Aug 2026 — REAL driver-app login as this route's driver
                (support tool: end a stuck route, badge a pause, …). */}
            {route.driver?.id && (
              <Popconfirm
                title="Log into the driver app as this driver?"
                description="Opens a new tab with a REAL driver session — everything you do there counts as the driver (4h, audited)."
                okText="Open driver app"
                cancelText={t('common:cancel')}
                onConfirm={() => openDriverAppAsDriver(route.driver.id, message)}
              >
                <Button>Log in as driver</Button>
              </Popconfirm>
            )}
            <Dropdown menu={{ items: actionMenuItems, onClick: onActionMenuClick }} trigger={['click']}>
              <Button icon={<DownOutlined />} iconPosition="end" loading={resendingWh || closingRoute}>
                Actions
              </Button>
            </Dropdown>
            {routeDate && (
              <Link to={`/admin/route-plans?date=${routeDate}`}>
                <Button type="primary" icon={<CalendarOutlined />}>
                  View day plan
                </Button>
              </Link>
            )}
          </div>
        </div>

        <DeleteRouteModal
          open={deleteOpen}
          routeId={route?.id}
          count={1}
          dispatched={route?.status?.toUpperCase() === 'DISPATCHED'}
          onCancel={() => setDeleteOpen(false)}
          onConfirm={handleConfirmDelete}
        />

        {/* OVO-203 — change a route's driver without re-optimizing (no-show swap) */}
        <Modal
          title="Change driver"
          open={changeDriverOpen}
          onCancel={() => setChangeDriverOpen(false)}
          onOk={handleChangeDriver}
          confirmLoading={savingDriver}
          okText="Reassign"
          okButtonProps={{
            disabled: !pickedDriverId || pickedDriverId === route?.driver?.id,
          }}
        >
          <p style={{ color: 'var(--text-secondary)', marginTop: 0 }}>
            Move this route to another driver. The vehicle, stop order and times are kept — no re-optimization. The new
            driver sees it in their app; the previous one no longer does.
          </p>
          <Select
            showSearch
            style={{ width: '100%' }}
            placeholder="Select a driver"
            optionFilterProp="label"
            value={pickedDriverId}
            onChange={setPickedDriverId}
            options={drivers.map((d) => ({
              value: d.id,
              label: d.name + (d.phone ? ` · ${d.phone}` : ''),
            }))}
          />
        </Modal>

        {/* #135 — the driver forgot to end the route; the system closed it at the
            last validated stop + 5 min. Visible + undoable. */}
        {route.autoClosedAt && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            data-testid="auto-close-banner"
            message={t('autoClose.banner')}
            description={t('autoClose.bannerDesc', {
              end: route.actualShiftEnd ? moment(route.actualShiftEnd).format(DAYTIME_FORMAT) : '—',
              at: moment(route.autoClosedAt).format(DAYTIME_FORMAT),
            })}
            action={
              <Button size="small" onClick={undoAutoClose}>
                {t('autoClose.undo')}
              </Button>
            }
          />
        )}

        {/* OVO-260 — "Corrected time" flag. The shift end was auto-corrected to
            the last validated stop, so the reviewer must be able to see that
            (and what it was before) without digging into the audit trail. */}
        {route.shiftCorrectedAt && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('inconsistencies.correctedBanner')}
            description={
              <span>
                {t('inconsistencies.correctedBannerDesc', {
                  from: route.shiftCorrectedFromEnd ? moment(route.shiftCorrectedFromEnd).format(DAYTIME_FORMAT) : '—',
                  to: route.actualShiftEnd ? moment(route.actualShiftEnd).format(DAYTIME_FORMAT) : '—',
                })}
                {' · '}
                {moment(route.shiftCorrectedAt).format(DAYTIME_FORMAT)}
              </span>
            }
            action={
              <Button size="small" onClick={undoShiftCorrection}>
                {t('inconsistencies.undoCorrection')}
              </Button>
            }
          />
        )}

        {/* #105 (Sep 22 2026, CT) — a route whose stops are all handled but that
            was never ended, or a past-day route still in a working status: the
            operator close existed only inside the actions menu, and nobody found
            it (CT route 1071 stayed open for a week). */}
        {canCloseRoute &&
          (route.orders || []).length > 0 &&
          (() => {
            const HANDLED = ['COMPLETED', 'DELIVERY_FAILED', 'PICKUP_FAILED', 'SKIPPED', 'ERROR'];
            const open = (route.orders || []).filter((o) => !HANDLED.includes(String(o.status || '').toUpperCase()));
            const allHandled = open.length === 0;
            const dayOver = route.date && moment(route.date).isBefore(moment(), 'day');
            if (!allHandled && !dayOver) return null;
            return (
              <Alert
                type={allHandled ? 'success' : 'warning'}
                showIcon
                style={{ marginBottom: 16 }}
                message={
                  allHandled
                    ? t('closeBanner.allHandled')
                    : t('closeBanner.stillOpen', { date: moment(route.date).format('D MMM') })
                }
                description={
                  allHandled ? t('closeBanner.allHandledDesc') : t('closeBanner.stillOpenDesc', { count: open.length })
                }
                action={
                  <Button size="small" danger loading={closingRoute} onClick={handleOperatorCloseRoute}>
                    {t('closeBanner.cta')}
                  </Button>
                }
              />
            );
          })()}

        {/* Inconsistency banner — this route trips a detection check (shared
            with Monitoring → Inconsistencies). If an operator dismissed it as a
            false positive, show the muted note + Undo; otherwise the warning +
            a "Mark as false positive" action. */}
        {route.inconsistencyDismissedAt ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('inconsistencies.dismissedBanner')}
            description={
              <span>
                {route.inconsistencyDismissReason || t('inconsistencies.noReason')}
                {' · '}
                {moment(route.inconsistencyDismissedAt).format(DAYTIME_FORMAT)}
              </span>
            }
            action={
              <Button size="small" onClick={restoreInc}>
                {t('inconsistencies.undoFalsePositive')}
              </Button>
            }
          />
        ) : (
          route.inconsistency && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={(() => {
                const key =
                  route.inconsistency.type === 'route_end_before_last_delivery'
                    ? 'inconsistencies.types.endBeforeDelivery'
                    : route.inconsistency.type === 'start_missing_bike_pickup'
                      ? 'inconsistencies.types.startMissingPickup'
                      : 'inconsistencies.types.drift';
                return t(key);
              })()}
              description={(() => {
                const i = route.inconsistency;
                const fmt = (iso) => (iso ? moment(iso).format(DAYTIME_FORMAT) : '—');
                const hard = i.type === 'route_end_before_last_delivery';
                const isStart = i.type === 'start_missing_bike_pickup';
                if (isStart) {
                  return (
                    <span>
                      {t('inconsistencies.badgedStart')}: <strong>{fmt(i.recordedEnd)}</strong> ·{' '}
                      {t('inconsistencies.firstPickup')}: <strong>{fmt(i.referenceTime)}</strong> ·{' '}
                      {t('inconsistencies.offBy')}: <strong>{i.offByMins} min</strong>
                      {' — '}
                      {t('inconsistencies.bannerFixHint')}
                    </span>
                  );
                }
                const offBy = hard
                  ? t('inconsistencies.beforeDeliveryBy', { mins: i.offByMins })
                  : `${i.offByMins > 0 ? '+' : ''}${i.offByMins} min`;
                return (
                  <span>
                    {t('inconsistencies.recordedEnd')}: <strong>{fmt(i.recordedEnd)}</strong> ·{' '}
                    {t('inconsistencies.expectedEnd')}: <strong>{fmt(i.referenceTime)}</strong> ·{' '}
                    {t('inconsistencies.offBy')}: <strong>{offBy}</strong>
                    {' — '}
                    {t('inconsistencies.bannerFixHint')}
                  </span>
                );
              })()}
              action={
                <Button
                  size="small"
                  onClick={() => {
                    setIncDismissReason('');
                    setIncDismissOpen(true);
                  }}
                >
                  {t('inconsistencies.markFalsePositive')}
                </Button>
              }
            />
          )
        )}

        <Modal
          title={t('inconsistencies.markFalsePositive')}
          open={incDismissOpen}
          onCancel={() => setIncDismissOpen(false)}
          onOk={submitIncDismiss}
          okText={t('inconsistencies.markFalsePositive')}
          confirmLoading={incDismissSaving}
          cancelText={t('common:cancel')}
        >
          <p style={{ marginTop: 0, color: '#555' }}>{t('inconsistencies.markFalsePositiveDesc', { id: route.id })}</p>
          <Input.TextArea
            rows={3}
            autoFocus
            value={incDismissReason}
            onChange={(e) => setIncDismissReason(e.target.value)}
            placeholder={t('inconsistencies.reasonPlaceholder')}
          />
        </Modal>

        {/* KPI tiles — the driver's full route totals (the per-partner
            "{Partner} stats" cards below break them down). */}
        <div className="kpi-tiles">
          <Tooltip title="Bike pick-up (start) → bike return (end) — for payroll / time-tracking">
            <div className="kpi-tile">
              <div className="kpi-tile-label">Shift</div>
              <div className="kpi-tile-value">{formatMins(route.durations?.shiftMins)}</div>
              <div className="kpi-tile-sub">
                {route.actualShiftStart ? formatActualTime(route.actualShiftStart) : '—'} →{' '}
                {route.actualShiftEnd ? formatActualTime(route.actualShiftEnd) : '—'} · planned{' '}
                {route.shiftStart || '—'} → {route.shiftEnd || '—'}
              </div>
            </div>
          </Tooltip>
          <Tooltip title="First pickup (collection) → last delivery — for client billing">
            <div className="kpi-tile">
              <div className="kpi-tile-label">Client service</div>
              <div className="kpi-tile-value">{formatMins(route.durations?.clientServiceMins)}</div>
              <div className="kpi-tile-sub">first pickup → last delivery</div>
            </div>
          </Tooltip>
          <div className="kpi-tile">
            <div className="kpi-tile-label">Stops</div>
            <div className="kpi-tile-value">
              {mergedStops.length}
              <span className="kpi-tile-inline ok">{orderStatusCount(['COMPLETED'])} done</span>
            </div>
            <div className="kpi-tile-sub">
              {route.orders?.length || 0} orders · {orderStatusCount(['DELIVERY_FAILED', 'PICKUP_FAILED'])} failed ·{' '}
              {orderStatusCount(['SKIPPED'])} skipped
            </div>
          </div>
          <div className="kpi-tile">
            <div className="kpi-tile-label">Distance</div>
            <div className="kpi-tile-value">{formatKm(route.distance)}</div>
            <div className="kpi-tile-sub">planned by the optimizer</div>
          </div>
          {route.durations?.priseDeServiceMins != null && (
            <Tooltip title="Prise de service — bike pick-up / return + start & close overhead (shift − client service)">
              <div className="kpi-tile">
                <div className="kpi-tile-label">Prise de service</div>
                <div className="kpi-tile-value">{formatMins(route.durations?.priseDeServiceMins)}</div>
                <div className="kpi-tile-sub">shift − client service</div>
              </div>
            </Tooltip>
          )}
          {route.durations?.pausedMins > 0 && (
            <Tooltip title="Driver pauses — excluded from worked hours (shift) and from billable client service">
              <div className="kpi-tile">
                <div className="kpi-tile-label">Paused</div>
                <div className="kpi-tile-value">{formatMins(route.durations?.pausedMins)}</div>
                <div className="kpi-tile-sub">
                  {route.durations.pauseCount} pause{route.durations.pauseCount > 1 ? 's' : ''} · excluded
                </div>
              </div>
            </Tooltip>
          )}
          {/* OVO-106 — CO2 (hidden if the fleet has no factor set). */}
          {route.co2 && (
            <Tooltip
              title={`${route.co2.gPerKm} g CO₂-eq per km (Mobitool v3.1) · saved vs a reference vehicle at ${route.co2.baselineGPerKm} g/km`}
            >
              <div className={'kpi-tile' + (route.co2.savedKg > 0 ? ' green' : '')}>
                <div className="kpi-tile-label">{route.co2.savedKg > 0 ? 'CO₂ saved vs car' : 'CO₂'}</div>
                <div className="kpi-tile-value">
                  {route.co2.savedKg > 0 ? `−${route.co2.savedKg.toFixed(2)} kg` : `${route.co2.kg.toFixed(2)} kg`}
                </div>
                <div className="kpi-tile-sub">
                  {route.co2.kg.toFixed(2)} kg emitted · {route.co2.gPerKm} g/km
                </div>
              </div>
            </Tooltip>
          )}
          {(startDelayMins != null || route.shiftStart) && (
            <Tooltip title="Badged start vs the planned shift start (≤ 10 min counts as on time)">
              <div
                className={
                  'kpi-tile' +
                  (startDelayMins == null
                    ? ' bad'
                    : startDelayMins > 10
                      ? ' warn'
                      : startDelayMins < -10
                        ? ' warn'
                        : '')
                }
              >
                <div className="kpi-tile-label">Start punctuality</div>
                <div className="kpi-tile-value">
                  {startDelayMins == null
                    ? 'Not badged'
                    : Math.abs(startDelayMins) <= 10
                      ? 'On time'
                      : `${startDelayMins > 0 ? '+' : '−'}${Math.abs(startDelayMins)} min`}
                </div>
                <div className="kpi-tile-sub">
                  {route.actualShiftStart ? `badged ${formatActualTime(route.actualShiftStart)}` : 'no start badge'} ·
                  planned {route.shiftStart || '—'}
                </div>
              </div>
            </Tooltip>
          )}
        </div>

        <div className="route-columns">
          <aside className="route-side">
            {/* Map */}
            {(route.polyline || markers.length > 0) && (
              <Card
                className={'map-card' + (mapFullscreen ? ' map-card--fullscreen' : '')}
                styles={{ body: { padding: 0 } }}
              >
                <div className="map-frame">
                  <GoogleMap markers={markers} polylines={polylines} active={activeStop} />
                  <Tooltip
                    title={
                      mapFullscreen
                        ? t('common:exitFullscreen', 'Exit full screen (Esc)')
                        : t('common:fullscreen', 'Full screen')
                    }
                    placement="left"
                  >
                    <Button
                      className="map-fullscreen-toggle"
                      icon={mapFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                      onClick={toggleMapFullscreen}
                      aria-label={mapFullscreen ? 'Exit full screen map' : 'Full screen map'}
                    />
                  </Tooltip>
                </div>
                {/* OVO-109 — Driver location summary line. Three states:
                  1. driverLastSeen present: show position + time-ago + context
                  2. route DISPATCHED but no events: "Not started yet"
                  3. route COMPLETED or no driver activity expected: hide
            */}
                {(() => {
                  const last = route.driverLastSeen;
                  const initials = driverInitials(route.driver?.name);
                  if (last?.lat != null && last?.lng != null) {
                    const ago = last.at ? moment(last.at).fromNow() : null;
                    const at = last.at ? moment(last.at).format(DAYTIME_FORMAT) : null;
                    const ctx =
                      last.source === 'OrderLog' && last.orderCode
                        ? `after ${String(last.eventStatus || '')
                            .replace(/_/g, ' ')
                            .toLowerCase()} of ${last.orderCode}`
                        : last.source === 'RouteLog'
                          ? `after route ${String(last.eventStatus || '').toLowerCase()}`
                          : null;
                    return (
                      <div className="driver-last-seen driver-last-seen--live">
                        <DriverPin initials={initials} />
                        <div className="driver-last-seen-text">
                          <strong>{route.driver?.name || 'Driver'}</strong>
                          <span className="driver-last-seen-meta">
                            Last seen {ago}
                            {at ? ` (${at})` : ''}
                            {ctx ? ` — ${ctx}` : ''}
                          </span>
                        </div>
                      </div>
                    );
                  }
                  if (route.status === 'DISPATCHED') {
                    return (
                      <div className="driver-last-seen driver-last-seen--idle">
                        <span className="driver-last-seen-dot" />
                        <div className="driver-last-seen-text">
                          <strong>{route.driver?.name || 'Driver'}</strong>
                          <span className="driver-last-seen-meta">Not started yet</span>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </Card>
            )}

            {/* Shift + route details (was the "Summary" Descriptions table). */}
            <Card
              className="shift-card"
              title="Shift"
              extra={
                shiftNeedsFix ? (
                  <Button danger size="small" type="primary" icon={<EditOutlined />} onClick={openShiftEditor}>
                    {t('fixShiftTimes')}
                  </Button>
                ) : (
                  <Button size="small" icon={<EditOutlined />} onClick={openShiftEditor}>
                    {t('editShiftTimes')}
                  </Button>
                )
              }
            >
              <div className="kv-grid">
                <div>
                  <div className="kv-label">Start</div>
                  <div className="kv-value">
                    <b className={route.actualShiftStart ? '' : 'missing'}>
                      {formatShiftBoundary(route.actualShiftStart)}
                    </b>
                    <span className="kv-sub">planned {route.shiftStart || '—'}</span>
                  </div>
                </div>
                <div>
                  <div className="kv-label">End</div>
                  <div className="kv-value">
                    <b className={route.actualShiftEnd ? '' : 'missing'}>{formatShiftBoundary(route.actualShiftEnd)}</b>
                    <span className="kv-sub">planned {route.shiftEnd || '—'}</span>
                  </div>
                </div>
                <div>
                  <div className="kv-label">Pauses</div>
                  <div className="kv-value">
                    <b>{route.durations?.pauseCount || 0}</b>
                    <span className="kv-sub">{formatMins(route.durations?.pausedMins || 0)}</span>
                  </div>
                </div>
                <div>
                  <div className="kv-label">Prise de service</div>
                  <div className="kv-value">
                    <b>{formatMins(route.durations?.priseDeServiceMins)}</b>
                    <span className="kv-sub">hub ↔ first / last stop</span>
                  </div>
                </div>
              </div>
              <div className="kv-list">
                <div>
                  <span className="kv-label">Start</span>
                  <span>{route.startAddress || '-'}</span>
                </div>
                <div>
                  <span className="kv-label">End</span>
                  <span>{route.endAddress || '-'}</span>
                </div>
                <div>
                  <span className="kv-label">Dispatched</span>
                  <span>{formatDateTime(route.dispatchedAt)}</span>
                </div>
                <div>
                  <span className="kv-label">Created</span>
                  <span>{formatDateTime(route.createdAt)}</span>
                </div>
                <div>
                  <span className="kv-label">Updated</span>
                  <span>{formatDateTime(route.updatedAt)}</span>
                </div>
              </div>
            </Card>
          </aside>

          <div className="route-main">
            {/* Steps */}
            <Card title={`Stops (${mergedStops.length})`} className="steps-card">
              <Table
                dataSource={stepsWithDriver}
                columns={stepColumns}
                rowKey="id"
                pagination={false}
                scroll={{ x: 1080 }}
                size="small"
                locale={{ emptyText: 'No stops' }}
                // Clicking a row blinks its pin on the map (toggle off on re-click).
                onRow={(rec) => ({
                  onClick: () => {
                    const id = stopRowMapId(rec);
                    setActiveStop((prev) => (prev?.id === id ? null : { id }));
                  },
                  style: { cursor: 'pointer' },
                })}
                // Highlight the synthetic driver row + the START/END boundary
                // rows so they stand out from the real stops in the sequence; plus
                // the currently-clicked row (its pin is blinking on the map).
                rowClassName={(rec) =>
                  [
                    activeStop?.id === stopRowMapId(rec) ? 'active-stop-row' : '',
                    // Both legs of the hovered order (the row under the mouse
                    // and its counterpart) — the correlation at a glance.
                    hoverOrderId != null &&
                    !rec.isBoundaryRow &&
                    !rec.isDriverRow &&
                    (rec.orderIds || []).includes(hoverOrderId)
                      ? 'linked-stop-row'
                      : '',
                    rec.isDriverRow ? 'driver-row' : rec.isBoundaryRow ? 'boundary-row' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')
                }
              />
            </Card>
          </div>
        </div>

        {/* OVO-268 — how Client service is computed, in plain words. Sits
            between the route totals and the per-partner cards so an operator
            (or Felipe) reading the numbers finds the rules right where the
            question arises. */}
        {route.partnerDurations?.length > 0 && (
          <Alert
            type="info"
            showIcon
            className="client-service-explainer"
            style={{ margin: '12px 0' }}
            message={t('clientServiceExplainer.title')}
            description={
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                <li>{t('clientServiceExplainer.total')}</li>
                <li>{t('clientServiceExplainer.perPartner')}</li>
                <li>{t('clientServiceExplainer.sum')}</li>
                <li>{t('clientServiceExplainer.gaps')}</li>
              </ul>
            }
          />
        )}

        {/* Per-partner billing breakdown — one "{Partner} stats" card per
            partner (full KPI shape). The route totals are the "Driver stats"
            card at the top (the main KPI summary), so there's no separate totals
            card here. Shown for ANY route with a partner (single or mutualised)
            so the presentation is consistent. Each partner's client-service is
            their own span; distance + CO2 are fair-share; the prise-de-service
            overhead is split proportionally. Billing basis for routes serving
            one or several clients on a tour (e.g. Ster + Bean). */}
        {route.partnerDurations?.length > 0 && (
          <>
            {route.partnerDurations.map((p) => (
              <Card
                key={p.partnerId}
                title={`${p.partnerName || `Partner #${p.partnerId}`} stats`}
                className="kpi-card partner-kpi-card"
              >
                {renderKpiGrid({
                  stops: p.stops,
                  orders: p.orders,
                  distanceMeters: (p.distanceKm || 0) * 1000,
                  clientServiceMins: p.clientServiceMins,
                  priseDeServiceMins: p.priseDeServiceMins,
                  co2: p.co2,
                })}
              </Card>
            ))}
          </>
        )}

        {/* OVO-128 — operator edit of the route's actual shift start/end. */}
        <Modal
          title={t('editShiftTimes')}
          open={shiftOpen}
          onCancel={() => setShiftOpen(false)}
          onOk={handleSubmitShift}
          confirmLoading={shiftSubmitting}
          okText={t('common:save')}
          destroyOnClose
        >
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>{t('editShiftTimesHint')}</p>
          {/* OVO-128 / OVO-129 (core) — one-click pre-fill from the
              optimizer-derived estimate; operator reviews before saving. */}
          <Button
            icon={<ThunderboltOutlined />}
            onClick={handleEstimateFromRoute}
            loading={estimating}
            style={{ marginBottom: 12 }}
            block
          >
            {t('estimateFromRoute')}
          </Button>
          <Form layout="vertical" form={shiftForm}>
            <Form.Item name="actualShiftStart" label={t('actualShiftStart')}>
              <DatePicker showTime format={DAYTIME_FORMAT} style={{ width: '100%' }} allowClear />
            </Form.Item>
            <Form.Item
              name="actualShiftEnd"
              label={t('actualShiftEnd')}
              dependencies={['actualShiftStart']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const start = getFieldValue('actualShiftStart');
                    if (value && start && value.isBefore(start)) {
                      return Promise.reject(new Error(t('shiftEndBeforeStart')));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <DatePicker showTime format={DAYTIME_FORMAT} style={{ width: '100%' }} allowClear />
            </Form.Item>
            <Form.Item name="note" label={t('noteOptional')}>
              <Input.TextArea rows={2} placeholder={t('shiftEditNotePlaceholder')} />
            </Form.Item>
          </Form>
        </Modal>

        {/* OVO-252 — reschedule a skipped/failed order from the stops table */}
        <Modal
          title={`Reschedule ${reschedTarget || ''}`}
          open={!!reschedTarget}
          okText="Reschedule"
          confirmLoading={rescheduling}
          onCancel={() => setReschedTarget(null)}
          onOk={async () => {
            if (!reschedDate) {
              message.error('Pick the new delivery date.');
              return;
            }
            setRescheduling(true);
            const { response, error } = await rescheduleOrder(reschedTarget, {
              deliveryDate: reschedDate.format('YYYY-MM-DD'),
            });
            setRescheduling(false);
            if (error) {
              message.error(error?.data?.message || 'Reschedule failed');
              return;
            }
            message.success(
              `Order rescheduled for ${reschedDate.format('YYYY-MM-DD')} — back in the unscheduled pool (attempt ${
                (response?.deliveryAttempts || 0) + 1
              }). Its stop leaves this route.`,
            );
            setReschedTarget(null);
            load();
          }}
        >
          <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            The order leaves this route and returns to the unscheduled pool as UNASSIGNED on the chosen date — plan it
            from the Route Plan as usual. The failed attempt stays in the order timeline and its attempts counter is
            incremented.
          </p>
          <Form layout="vertical">
            <Form.Item label="New delivery date" required>
              <DatePicker
                style={{ width: '100%' }}
                value={reschedDate}
                onChange={setReschedDate}
                disabledDate={(d) => d && d.isBefore(dayjs().startOf('day'))}
              />
            </Form.Item>
          </Form>
        </Modal>

        {/* Orders */}
        <Card title={`Orders (${route.orders?.length || 0})`} className="orders-card">
          <Table
            dataSource={route.orders || []}
            columns={orderColumns}
            rowKey="id"
            pagination={false}
            size="small"
            locale={{ emptyText: 'No orders' }}
          />
        </Card>

        {/* Collected items */}
        {route.collectedItemsByPartner?.length > 0 && (
          <Card title="Collected Items" className="collected-card">
            {route.collectedItemsByPartner.map((p) => (
              <Tag key={p.code} icon={<InboxOutlined />} color="orange" style={{ marginBottom: 4 }}>
                {p.code}: {p.count}
              </Tag>
            ))}
          </Card>
        )}

        {/* Return drop-off */}
        {route.returnDropoffConfirmedAt && (
          <Card title="Return Drop-off" className="return-dropoff-card">
            <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
              <Descriptions.Item label="Confirmed at">
                {formatDateTime(route.returnDropoffConfirmedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Address">{route.returnDropoffAddress || '-'}</Descriptions.Item>
              <Descriptions.Item label="Note" span={2}>
                {route.returnDropoffNote || '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}

        {/* Edit history — operator audit trail (shift-time corrections, …). */}
        <Collapse className="advanced-collapse" onChange={(keys) => keys?.length && loadAuditLog()}>
          <Collapse.Panel header={t('editHistory')} key="edit-history">
            {auditLoading && <Spin />}
            {!auditLoading && auditLog && auditLog.length === 0 && (
              <span style={{ color: 'var(--text-muted)' }}>{t('noEditHistory')}</span>
            )}
            {!auditLoading &&
              auditLog &&
              auditLog.map((entry) => {
                const who = entry.user?.name || entry.user?.email || `#${entry.userId}`;
                const fmt = (iso) => (iso ? moment(iso).format(DAYTIME_FORMAT) : '—');
                const changes = entry.changes || {};
                return (
                  <div key={entry.id} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: 13 }}>
                    <div style={{ color: 'var(--text-muted)' }}>
                      <Tag color="orange">{entry.event}</Tag>
                      {moment(entry.createdAt).format(DAYTIME_FORMAT)} · <strong>{who}</strong>
                    </div>
                    {Object.entries(changes).map(([field, v]) => (
                      <div key={field} style={{ marginTop: 4 }}>
                        <span style={{ color: 'var(--text-muted)' }}>{field}: </span>
                        <span style={{ textDecoration: 'line-through', color: '#bbb' }}>{fmt(v?.before)}</span>
                        {' → '}
                        <strong>{fmt(v?.after)}</strong>
                      </div>
                    ))}
                    {entry.note && (
                      <div style={{ marginTop: 4, fontStyle: 'italic', color: 'var(--text-secondary)' }}>
                        “{entry.note}”
                      </div>
                    )}
                  </div>
                );
              })}
          </Collapse.Panel>
        </Collapse>

        {/* Advanced */}
        <Collapse className="advanced-collapse">
          <Collapse.Panel header="Advanced (raw DB data)" key="advanced">
            <div className="json-tree">
              <JsonNode value={route} depth={0} />
            </div>
          </Collapse.Panel>
        </Collapse>
      </div>
      <CreateTicketModal
        open={ticketModalOpen}
        onClose={() => setTicketModalOpen(false)}
        prefill={{ routeId: route?.id, driverId: route?.driver?.id }}
      />
      <EtaBacktestModal routeId={route?.id} open={backtestOpen} onClose={() => setBacktestOpen(false)} />
      <Modal
        open={resendOpen}
        title="Resend partner webhooks"
        okText="Resend"
        cancelText={t('common:cancel')}
        confirmLoading={resendingWh}
        onOk={handleResendWebhooks}
        onCancel={() => setResendOpen(false)}
      >
        <p style={{ marginTop: 0 }}>What to re-send for this route&apos;s partner orders (order.dispatched):</p>
        <Radio.Group
          value={resendMode}
          onChange={(e) => setResendMode(e.target.value)}
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          {RESEND_MODES.map((m) => (
            <Radio key={m.value} value={m.value}>
              <strong>{m.label}</strong>
              <div style={{ color: '#616161', fontSize: 12 }}>{m.help}</div>
            </Radio>
          ))}
        </Radio.Group>
      </Modal>
      <RoutePausesModal
        route={route}
        open={pausesOpen}
        onClose={() => setPausesOpen(false)}
        onUpdated={(r) => r && setRoute(r)}
      />
    </div>
  );
};

export default RouteDetailPage;
